import { shapes } from '@joint/plus';
import React, { ChangeEvent, ReactElement, useCallback, useState } from 'react';

import ReactQuill from 'react-quill';
import { Switch, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useIntl } from 'react-intl';
import HelperCustomField from '../../HelperCustomField/HelperCustomField';
import Input from '../Input/Input';
import UpstreamFieldsHint from './UpstreamFieldsHint';
import { useBaseInspector } from './useBaseInspector';
import VariableAutocompletePicker from './VariableAutocompletePicker';

interface Props {
    cell: shapes.app.Message;
    chatbotFlowId: string;
}

interface InspectorPort {
    id: string;
    label: string;
    optionValue: string;
}

const cellProps = {
    label: ['attrs', 'label', 'text'],
    description: ['attrs', 'description', 'text'],
    icon: ['attrs', 'icon', 'xlinkHref'],
    portLabel: ['attrs', 'portLabel', 'text'],
    isTextRich: ['attrs', 'isTextRich', 'isTextRich'],
    // Item #4.5 (09/10/2026) — Resultado em variável.
    // Padrão canônico: `attrs.resultVariable.text` = NOME da variável (igual
    // InteractiveList/NativeAction). Backend lê e aplica via ISetVariableBoxService
    // no clique da option, com fallback: port.attrs.optionValue.text → portLabel.text.
    resultVariable: ['attrs', 'resultVariable', 'text'],
    optionValue: ['attrs', 'optionValue', 'text'],  // por port
};

const MessageInspector = (props: Props): ReactElement => {

    const { cell, chatbotFlowId } = props;
    const intl = useIntl();

    const [label, setLabel] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [icon, setIcon] = useState<string>('');
    const [ports, setPorts] = useState<InspectorPort[]>([]);
    const [canAddPort, setCanAddPort] = useState<boolean>(false);
    const [isTextRich, setIsTextRich] = useState<boolean>(false);

    // Item #4.5 — Resultado em variável (nome persistido; VariableAutocompletePicker
    // cuida da lista + criação inline, igual SetVariableInspector).
    const [resultVariable, setResultVariable] = useState<string>('');

    const assignFormPorts = useCallback((): void => {
        setCanAddPort(cell.canAddPort('out'));
        setPorts(cell.getGroupPorts('out').map(({ id }) => {
            return {
                id,
                label: cell.portProp(id, cellProps.portLabel) ?? '',
                optionValue: cell.portProp(id, cellProps.optionValue) ?? '',
            };
        }));
    }, [cell]);

    const assignFormFields = useCallback((): void => {
        setLabel(cell.prop(cellProps.label));
        setDescription(cell.prop(cellProps.description));
        setIcon(cell.prop(cellProps.icon));
        setIsTextRich(cell.prop(cellProps.isTextRich));
        setResultVariable(cell.prop(cellProps.resultVariable) ?? '');
        assignFormPorts();
    }, [cell, assignFormPorts]);

    const changeCellProp = useBaseInspector({ cell, assignFormFields });

    const addCellPort = (): void => {
        cell.addDefaultPort();
        assignFormPorts();
    };

    const removeCellPort = (portId: string): void => {
        cell.removePort(portId);
        assignFormPorts();
    };

    const changeCellPort = (port: InspectorPort, value: string): void => {
        cell.portProp(port.id, cellProps.portLabel, value);
    };

    const changeCellPortOptionValue = (port: InspectorPort, value: string): void => {
        cell.portProp(port.id, cellProps.optionValue, value);
    };

    return (
        <>

            <label>Label
                <Input type="text"
                       placeholder="Enter label"
                       value={label}
                       onChange={(e: ChangeEvent<HTMLInputElement>) => changeCellProp(cellProps.label, e.target.value)}
                       disabled={true}
                />
            </label>

            <div style={{ marginBottom: '8px', marginTop: '8px'}}>
                <Switch
                    value={isTextRich}
                    checked={isTextRich}
                    onChange={(e:any) => changeCellProp(cellProps.isTextRich, e?.target?.checked)}     
                />
                <label>É um texto rico?</label>
            </div>

            {
                isTextRich
                ? (
                    <label>Descrição
                        <div style={{ resize: "vertical", overflow: "auto", height: "250px", marginBottom: "24px", border: "1px solid #ccc" }}>
                            <ReactQuill
                                style={{ height: '100%' }}
                                name="description"
                                fullWidth
                                variant="outlined"
                                id="descriprion"
                                value={description}
                                onChange={(value: any) => changeCellProp(cellProps.description, value)}
                            />
                        </div>
                    </label>
                )
                : (
                    <label>Descrição
                        <Input 
                            type="text"
                            placeholder="Enter description"
                            value={description}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => changeCellProp(cellProps.description, e.target.value)}
                        />
                    </label>
                )
            }

            <HelperCustomField
                isRichText={isTextRich}
                description={description}
                param1={cellProps.description}
                onChangeDescription={changeCellProp}
                chatbotFlowId={chatbotFlowId}
            />

            {/* 13/10/2026 — Hint de dot-notation quando description referencia variável upstream. */}
            <UpstreamFieldsHint
                cell={cell}
                textValue={description}
                compact
                onInsert={(token) => {
                    const next = (description ?? '') + token;
                    setDescription(next);
                    changeCellProp(cellProps.description, next);
                }}
            />

            {/* Item #4.5 — Resultado em variável (09/10/2026).
                Opcional. Se configurada, no clique da option o backend seta esta
                variável com o 'Valor' da port (ou com o label, se Valor vazio).
                Caixa Condition 3 níveis abaixo passa a conseguir avaliar sem precisar
                de uma caixa SetVariable intermediária. 100% retrocompat: vazio = comportamento igual ao de hoje. */}
            <div style={{ marginBottom: 16, marginTop: 16, borderTop: '1px dashed #D4D4D4', paddingTop: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    {intl.formatMessage({ id: 'message.resultVariable.label', defaultMessage: 'Resultado em variável (opcional)' })}
                    <Tooltip title={intl.formatMessage({ id: 'message.resultVariable.help', defaultMessage: 'Quando o cliente clicar numa das opções, o valor dessa opção é salvo nesta variável. A variável pode ser reutilizada mais à frente no fluxo — por exemplo, referenciada em qualquer caixa de Condição abaixo pra rotear, ou devolvida pro cliente em caixas de mensagem.' })}>
                        <InfoOutlinedIcon fontSize="small" style={{ color: '#777', cursor: 'help' }}/>
                    </Tooltip>
                </label>
                <VariableAutocompletePicker
                    value={resultVariable}
                    onChange={(name) => {
                        setResultVariable(name);
                        changeCellProp(cellProps.resultVariable, name);
                    }}
                    placeholder={intl.formatMessage({ id: 'message.resultVariable.placeholder', defaultMessage: 'Selecione uma variável (ou deixe vazio)' })}
                />
            </div>

            <div className="ports">
                <div className="out-ports-bar">
                    <span>Opções</span>
                    <button disabled={!canAddPort}
                            onClick={addCellPort}
                            className="add-port"
                            data-tooltip="Add Output Port">
                    </button>
                </div>
                {ports.map(port => {
                    return (
                        <div key={port.id} className="port" style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                            <div style={{ display: 'flex', gap: 4 }}>
                                <Input defaultValue={port.label}
                                       onChange={(e: ChangeEvent<HTMLInputElement>) => changeCellPort(port, e.target.value)}
                                />
                                <button className="remove-port"
                                        onClick={() => removeCellPort(port.id)}
                                        data-tooltip="Remove Output Port">
                                </button>
                            </div>
                            {resultVariable && (
                                <Input defaultValue={port.optionValue}
                                       placeholder={intl.formatMessage({ id: 'message.optionValue.placeholder', defaultMessage: 'Valor (se vazio, usa o rótulo)' })}
                                       onChange={(e: ChangeEvent<HTMLInputElement>) => changeCellPortOptionValue(port, e.target.value)}
                                       style={{ fontSize: '0.85em', opacity: 0.9 }}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default MessageInspector;
